import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { FriendService } from '@/api/services/friendService';
import { FriendInfo, SearchParams } from '@/api/types';
import { useChatStore } from './useChatStore';

interface FriendState {
  // 好友列表数据
  friendList: FriendInfo[];
  friendMap: Map<string, FriendInfo>; // 用于快速查找的映射表
  friendListLoading: boolean;
  friendListError: string;

  // 屏蔽列表数据
  blockedList: FriendInfo[];
  blockedMap: Map<string, FriendInfo>; // 用于快速查找的映射表
  blockedListLoading: boolean;
  blockedListError: string;

  // 操作方法
  getFriendList: (params?: SearchParams) => Promise<Map<string, FriendInfo>>;
  getBlockedList: () => Promise<void>;
  refreshFriendList: () => Promise<void>;
  refreshBlockedList: () => Promise<void>;
  clearErrors: () => void;

  // 本地状态更新方法（用于操作后的即时更新）
  addFriendToList: (friend: FriendInfo) => void;
  removeFriendFromList: (friendId: string) => void;
  addToBlockedList: (friend: FriendInfo) => void;
  removeFromBlockedList: (friendId: string) => void;
  updateFriendInList: (friendId: string, updates: Partial<FriendInfo>) => void;
}

/**
 * 好友数据管理 Store
 * 管理好友列表和屏蔽列表的状态
 */
export const useFriendStore = create<FriendState>()(
  subscribeWithSelector((set, get) => ({
    // 初始状态
    friendList: [],
    friendMap: new Map<string, FriendInfo>(),
    friendListLoading: false,
    friendListError: '',

    blockedList: [],
    blockedMap: new Map<string, FriendInfo>(),
    blockedListLoading: false,
    blockedListError: '',

    /**
     * 获取好友列表
     * @param params 搜索参数
     */
    getFriendList: async (params?: SearchParams): Promise<Map<string, FriendInfo>> => {
      set({ friendListLoading: true, friendListError: '' });

      try {
        const result = await FriendService.getFriendList(params);
        const friendMap = new Map(result.data.map(friend => [friend.id, friend]));
        set({
          friendList: result.data,
          friendMap,
          friendListLoading: false
        });
        return friendMap;
      } catch (error: any) {
        console.error('获取好友列表失败:', error);
        set({
          friendListError: error?.err_msg || '获取好友列表失败',
          friendListLoading: false
        });
        return new Map<string, FriendInfo>();
      }
    },

    /**
     * 获取屏蔽列表
     */
    getBlockedList: async () => {
      set({ blockedListLoading: true, blockedListError: '' });

      try {
        const result = await FriendService.getBlockedList();
        const blockedMap = new Map(result.data.map(blocked => [blocked.id, blocked]));
        set({
          blockedList: result.data,
          blockedMap,
          blockedListLoading: false
        });
      } catch (error: any) {
        console.error('获取屏蔽列表失败:', error);
        set({
          blockedListError: error?.err_msg || '获取屏蔽列表失败',
          blockedListLoading: false
        });
      }
    },

    /**
     * 刷新好友列表
     */
    refreshFriendList: async () => {
      await get().getFriendList();
    },

    /**
     * 刷新屏蔽列表
     */
    refreshBlockedList: async () => {
      await get().getBlockedList();
    },

    /**
     * 清除所有错误信息
     */
    clearErrors: () => {
      set({
        friendListError: '',
        blockedListError: ''
      });
    },

    /**
     * 添加好友到列表（本地更新）
     * @param friend 好友信息
     */
    addFriendToList: (friend: FriendInfo) => {
      set(state => {
        const newFriendMap = new Map(state.friendMap);
        newFriendMap.set(friend.id, friend);
        return {
          friendList: [...state.friendList, friend],
          friendMap: newFriendMap
        };
      });
    },

    /**
     * 从好友列表中移除（本地更新）
     * @param friendId 好友ID
     */
    removeFriendFromList: (friendId: string) => {
      set(state => {
        const newFriendMap = new Map(state.friendMap);
        newFriendMap.delete(friendId);
        return {
          friendList: state.friendList.filter(friend => friend.id !== friendId),
          friendMap: newFriendMap
        };
      });
    },

    /**
     * 添加到屏蔽列表（本地更新）
     * @param friend 被屏蔽的用户信息
     */
    addToBlockedList: (friend: FriendInfo) => {
      set(state => {
        const newFriendMap = new Map(state.friendMap);
        const newBlockedMap = new Map(state.blockedMap);
        newFriendMap.delete(friend.id);
        newBlockedMap.set(friend.id, friend);
        return {
          blockedList: [...state.blockedList, friend],
          blockedMap: newBlockedMap,
          // 同时从好友列表中移除
          friendList: state.friendList.filter(f => f.id !== friend.id),
          friendMap: newFriendMap
        };
      });

      // 同时移除相关的私聊会话
      const chatStore = useChatStore.getState();
      const conversationToRemove = chatStore.conversations.find((conv: any) => 
        conv.session_type === 'direct' && 
        conv.participants?.some((p: any) => p.id === friend.id)
      );
      if (conversationToRemove) {
        chatStore.removeConversation(conversationToRemove.id);
      }
    },

    /**
     * 从屏蔽列表中移除（本地更新）
     * @param friendId 用户ID
     */
    removeFromBlockedList: (friendId: string) => {
      set(state => {
        const newBlockedMap = new Map(state.blockedMap);
        newBlockedMap.delete(friendId);
        return {
          blockedList: state.blockedList.filter(blocked => blocked.id !== friendId),
          blockedMap: newBlockedMap
        };
      });

      // 同时移除相关的私聊会话
      const chatStore = useChatStore.getState();
      const conversationToRemove = chatStore.conversations.find((conv: any) => 
        conv.session_type === 'direct' && 
        conv.participants?.some((p: any) => p.id === friendId)
      );
      if (conversationToRemove) {
        chatStore.removeConversation(conversationToRemove.id);
      }
    },

    /**
     * 更新好友列表中的好友信息（本地更新）
     * @param friendId 好友ID
     * @param updates 更新的字段
     */
    updateFriendInList: (friendId: string, updates: Partial<FriendInfo>) => {
      set(state => {
        const newFriendMap = new Map(state.friendMap);
        const existingFriend = newFriendMap.get(friendId);
        if (existingFriend) {
          const updatedFriend = { ...existingFriend, ...updates };
          newFriendMap.set(friendId, updatedFriend);
        }
        return {
          friendList: state.friendList.map(friend =>
            friend.id === friendId
              ? { ...friend, ...updates }
              : friend
          ),
          friendMap: newFriendMap
        };
      });
    }
  }))
);
